/**
 * Registro central de idiomas da interface do Zoraxy.
 *
 * Hoje o produto é entregue apenas em português do Brasil. Este arquivo existe
 * para que habilitar um novo idioma seja uma alteração de UMA linha, e não uma
 * caça a `locale` espalhado pelo código.
 *
 * ---------------------------------------------------------------------------
 * COMO HABILITAR UM NOVO IDIOMA (ex.: espanhol)
 * ---------------------------------------------------------------------------
 * 1. Traduza os arquivos de mensagem do idioma:
 *      app/javascript/dashboard/i18n/locale/es/*.json   (painel)
 *      app/javascript/widget/i18n/locale/es.json        (widget do site)
 *      app/javascript/survey/i18n/locale/es.json        (pesquisa CSAT)
 *      config/locales/es.yml                            (back-end Rails)
 *    Inclua também os catálogos de dados traduzidos, se quiser cobertura total:
 *      .../locale/es/countries.json, languages.json, timezones.json
 * 2. Acrescente o código a ENABLED_LOCALES abaixo.
 * 3. Garanta que o mesmo código esteja com `enabled: true` em
 *    config/initializers/languages.rb (é ele que alimenta o seletor da tela
 *    Configurações da conta).
 *
 * Nada mais precisa ser alterado: o seletor de idioma, a resolução do locale
 * ativo e o fallback passam todos por este módulo.
 */

/** Idioma padrão e também o fallback de qualquer chave ausente. */
export const DEFAULT_LOCALE = 'pt_BR';

/**
 * Idiomas oferecidos ao usuário. Manter apenas idiomas com tradução revisada:
 * um idioma listado aqui sem tradução completa mostra chaves em português
 * (fallback) misturadas com o idioma escolhido.
 */
export const ENABLED_LOCALES = [DEFAULT_LOCALE];

/**
 * @param {string} locale código do idioma (ex.: 'pt_BR', 'es')
 * @returns {boolean} true se o idioma está liberado para uso na interface
 */
export const isLocaleEnabled = locale => ENABLED_LOCALES.includes(locale);

/**
 * Resolve o idioma que deve ficar ativo. Qualquer valor desconhecido,
 * desabilitado ou vazio cai no idioma padrão — assim uma conta com `locale`
 * antigo no banco nunca deixa a interface meio traduzida.
 *
 * @param {string} [locale] código salvo na conta ou no perfil
 * @returns {string} código de idioma seguro para atribuir ao vue-i18n
 */
export const resolveLocale = locale =>
  isLocaleEnabled(locale) ? locale : DEFAULT_LOCALE;

/**
 * Filtra a lista de idiomas vinda do back-end (`window.globalConfig
 * .enabledLanguages`) para exibir no seletor apenas o que está realmente
 * traduzido.
 *
 * @param {Array<{iso_639_1_code: string}>} languages
 * @returns {Array<{iso_639_1_code: string}>}
 */
export const filterEnabledLanguages = (languages = []) =>
  languages.filter(language => isLocaleEnabled(language?.iso_639_1_code));

export default {
  DEFAULT_LOCALE,
  ENABLED_LOCALES,
  isLocaleEnabled,
  resolveLocale,
  filterEnabledLanguages,
};
